import i18n, { isSupportedLanguage } from '../../../i18n';
import type { ELanguages } from '../../../i18n';
import { getTenants, type Tenant } from '../../../api/organizations';
import type { AnyAction, Dispatch } from '@reduxjs/toolkit';
import type { AppThunk, RootState } from '../../../store';
import { changeBaseUrl, getResolvedApiUrl } from '../../../apis/utils/provider';
import { isValidRegion, setRegion } from '../../../config/region';
import { isMultiOrgFeatureEnabled } from '../../../utils/features';
import {
  clearGlobalTokens,
  type GlobalTokens,
  saveActiveTenantIdToStorage,
  saveGlobalTokens,
} from '../../../utils/tenantSessionStorage';
import { activateSessionByTenantId, loadPerTenantTokens } from './tenantTokens';
import { getUserById, getUserSettings } from '../../../api/users';
import { mapFromPreferredLanguageToELanguages } from '../../../apis/userAPI/helpers';
import { fetchTotalUnread } from '../../ChatsList/chatListSlice/actionCreators';
import { refreshUserPermissions } from '../../Login/LoginSlice/actionCreators';
import { safeParseJwtPayload } from '../../../utils/jwt';
import { setIsSwitchingTenantSession, setUser } from '../../Login/LoginSlice';
import { setItem } from '../../../utils/storage';
import {
  clearTenant,
  pruneTenantSession,
  selectActiveTenantId,
  selectTenant,
  selectTenantSessionById,
  setActiveTenantId,
  setSessionIndicators,
  setTenant,
  updateTenantSessionMetadata,
  persistTenantSessions,
} from '.';
import { resetTenantScopedSlices } from './resetTenantScoped';
import { syncInactiveSessionAlarms } from './syncSessionIndicators';
import { OVERLAY_FADE_IN_MS } from '../../../components/TenantSwitchingOverlay/TenantSwitchingOverlay.styles';

type TenantSnapshot = {
  activeTenantId: number | null;
  globalTokens: GlobalTokens | null;
  tenantRow: Tenant | null;
  region: string | null;
};

export const applyTenantRegion = (region: string): void => {
  if (!isValidRegion(region)) return;
  setRegion(region);
  changeBaseUrl(getResolvedApiUrl(region));
};

const captureTenantSnapshot = (state: RootState): TenantSnapshot => {
  const activeTenantId = selectActiveTenantId(state);
  return {
    activeTenantId,
    globalTokens: activeTenantId != null ? loadPerTenantTokens(activeTenantId) : null,
    tenantRow: selectTenant(state),
    region:
      activeTenantId != null
        ? (selectTenantSessionById(activeTenantId)(state)?.region ?? null)
        : null,
  };
};

const restoreFromSnapshot = (dispatch: Dispatch<AnyAction>, snap: TenantSnapshot): void => {
  if (snap.globalTokens) {
    saveGlobalTokens(snap.globalTokens);
  } else {
    clearGlobalTokens();
  }
  dispatch(setActiveTenantId(snap.activeTenantId));
  saveActiveTenantIdToStorage(snap.activeTenantId);
  if (snap.tenantRow) dispatch(setTenant(snap.tenantRow));
  if (snap.region) applyTenantRegion(snap.region);
};

type SwitchActiveOrganizationResult =
  | { ok: true }
  | { ok: false; hasPrunedSession: boolean; hasAlertedUser: boolean };

/** Switches active tenant, rolling back all side effects on failure. */
export const switchActiveOrganization =
  (targetTenantId: number): AppThunk<Promise<SwitchActiveOrganizationResult>> =>
  async (dispatch, getState) => {
    if (!isMultiOrgFeatureEnabled()) {
      return {
        ok: false,
        hasPrunedSession: false,
        hasAlertedUser: false,
      };
    }

    const state = getState();
    if (selectActiveTenantId(state) === targetTenantId) {
      return {
        ok: true,
      };
    }

    const targetSession = selectTenantSessionById(targetTenantId)(state);
    if (!targetSession) {
      return {
        ok: false,
        hasPrunedSession: false,
        hasAlertedUser: false,
      };
    }

    const snapshot = captureTenantSnapshot(state);
    const rollback = () => restoreFromSnapshot(dispatch, snapshot);

    dispatch(
      setIsSwitchingTenantSession({
        isSwitching: true,
        tenantName: targetSession.tenantName,
      })
    );

    // Let React flush the overlay mount + useEffect so the CSS fade-in has
    // actually started before the timer begins, then wait for it to complete.
    await new Promise(resolve => requestAnimationFrame(resolve));
    // Wait for the overlay to fully cover before mutating tenant-scoped state,
    // otherwise Redux subscribers re-render with the new tenant during the
    // overlay's fade-in and briefly bleed through.
    await new Promise(resolve => setTimeout(resolve, OVERLAY_FADE_IN_MS));

    try {
      if (!activateSessionByTenantId(targetTenantId)) {
        rollback();
        dispatch(pruneTenantSession({ tenantId: targetTenantId, alertUser: true }));
        return {
          ok: false,
          hasAlertedUser: true,
          hasPrunedSession: true,
        };
      }

      dispatch(setActiveTenantId(targetTenantId));
      saveActiveTenantIdToStorage(targetTenantId);

      if (targetSession.region && targetSession.region !== snapshot.region) {
        applyTenantRegion(targetSession.region);
      }

      dispatch(resetTenantScopedSlices());
      dispatch(clearTenant());

      const tenantsResponse = await getTenants();
      if (tenantsResponse.error) {
        rollback();
        // 401/403: session revoked server-side. Prune and signal removed-invalid
        // so the caller continues to the next candidate.
        const status = tenantsResponse.error.error?.httpStatus;
        if (status === 401 || status === 403) {
          dispatch(pruneTenantSession({ tenantId: targetTenantId, alertUser: true }));
          return {
            ok: false,
            hasPrunedSession: true,
            hasAlertedUser: true,
          };
        } else {
          return {
            ok: false,
            hasPrunedSession: false,
            hasAlertedUser: false,
          };
        }
      }
      const nextTenantRowExact = tenantsResponse.data.data.find(t => t.id === targetTenantId);
      const nextTenantRow: Tenant = nextTenantRowExact ?? {
        id: targetSession.tenantId,
        name: targetSession.tenantName,
        logoUrl: targetSession.tenantLogo,
        websiteUrl: null,
      };
      dispatch(setTenant(nextTenantRow));

      const accessToken = loadPerTenantTokens(targetTenantId)?.accessToken ?? null;
      const tokenUserId = Number(safeParseJwtPayload(accessToken || '')?.sub ?? NaN);
      if (!Number.isFinite(tokenUserId) || tokenUserId <= 0) {
        rollback();
        dispatch(pruneTenantSession({ tenantId: targetTenantId, alertUser: true }));
        return {
          ok: false,
          hasPrunedSession: true,
          hasAlertedUser: true,
        };
      }

      const userResponse = await getUserById({
        userId: tokenUserId,
        tenantId: targetTenantId,
      });
      if (userResponse.error) {
        rollback();
        const status = userResponse.error.error?.httpStatus;
        if (status === 401 || status === 403) {
          dispatch(pruneTenantSession({ tenantId: targetTenantId, alertUser: true }));
          return {
            ok: false,
            hasPrunedSession: true,
            hasAlertedUser: true,
          };
        } else {
          return {
            ok: false,
            hasPrunedSession: false,
            hasAlertedUser: false,
          };
        }
      }
      const nextUser = userResponse.data.data;
      dispatch(setUser(nextUser));

      await Promise.all([dispatch(refreshUserPermissions()), dispatch(fetchTotalUnread(true))]);

      dispatch(
        updateTenantSessionMetadata({
          tenantId: targetTenantId,
          metadata: {
            tenantName: nextTenantRowExact?.name?.trim() ?? targetSession.tenantName,
            tenantLogo:
              nextTenantRowExact != null
                ? (nextTenantRowExact.logoUrl ?? null)
                : targetSession.tenantLogo,
            email: nextUser?.email ?? targetSession.email,
          },
        })
      );
      // Clear the alarm flag for the tenant we just switched into — the user
      // is now viewing it, so the cross-org indicator must disappear.
      dispatch(
        setSessionIndicators({
          tenantId: targetTenantId,
          hasActiveAlarm: false,
          hasUnreadMessages: false,
          hasActiveImOk: false,
        })
      );
      persistTenantSessions(getState());

      // Reconcile alarm flags for the other (now inactive) sessions. Fire and
      // forget — this is best-effort drift correction, not a blocker.
      dispatch(syncInactiveSessionAlarms()).catch(error => {
        console.error('[switchActiveOrganization] syncInactiveSessionAlarms failed', {
          targetTenantId,
          error,
        });
      });

      await applyPreferredLanguageForUser(nextUser.id, targetTenantId);
      return {
        ok: true,
      };
    } catch (error) {
      console.error('[switchActiveOrganization] unexpected error', error);
      rollback();
      return {
        ok: false,
        hasPrunedSession: false,
        hasAlertedUser: false,
      };
    } finally {
      dispatch(setIsSwitchingTenantSession({ isSwitching: false }));
    }
  };

async function applyPreferredLanguageForUser(userId: number, tenantId: number): Promise<void> {
  try {
    const settingsResponse = await getUserSettings({ userId, tenantId });
    const preferredLanguage = settingsResponse.error
      ? null
      : (settingsResponse.data.data.preferredLanguage ?? null);
    if (!preferredLanguage) return;

    const mapped = mapFromPreferredLanguageToELanguages(
      preferredLanguage as Parameters<typeof mapFromPreferredLanguageToELanguages>[0]
    );
    const resolved: ELanguages | null = mapped && isSupportedLanguage(mapped) ? mapped : null;
    if (!resolved || i18n.language === resolved) return;

    setItem('language', resolved);
    i18n.changeLanguage(resolved);
  } catch (error) {
    console.warn('[switchActiveOrganization] applyPreferredLanguage failed', error);
  }
}
